import { InlineBanner } from '@7shifts/sous-chef-base';
import { useTranslation } from '@src/i18n';
import './OfflineBanner.css';

interface OfflineBannerProps {
    /** Whole days remaining before lockout, or null when not offline. */
    remainingDays: number | null;
    /** When true, appends the time-limit ("X more days") copy to the banner. */
    isApproaching: boolean;
}

/**
 * Persistent gold warning banner shown on the punch pad's left panel while the
 * terminal is offline (API-4380). When the offline time limit is approaching,
 * it expands with a "X more days" countdown. The banner cannot be dismissed —
 * it stays for the whole offline session.
 */
export function OfflineBanner({
    remainingDays,
    isApproaching
}: OfflineBannerProps) {
    const { t } = useTranslation();

    const showCountdown = isApproaching && remainingDays !== null;
    const daysText =
        remainingDays === 1
            ? t('offline.banner.daysSingular')
            : t('offline.banner.daysPlural', { count: remainingDays ?? 0 });

    const body = showCountdown ? (
        <>
            {t('offline.banner.body')} {t('offline.banner.timeLimitPrefix')}
            <strong>{daysText}</strong>
            {t('offline.banner.timeLimitSuffix')}
        </>
    ) : (
        t('offline.banner.body')
    );

    return (
        <div className="offline-banner">
            <InlineBanner
                theme="warning"
                title={t('offline.banner.heading')}
                testId="offline-banner"
            >
                {body}
            </InlineBanner>
        </div>
    );
}
